import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2 } from "lucide-react";
import { listShopCategories, listShopProducts } from "@/lib/shop.functions";
import { useHideCommerce } from "@/lib/native-app";
import { GiftMemberships } from "@/components/shop/GiftMemberships";
import { AffiliateDisclosure, CategoryIcon, ShopFilter, ShopGrid } from "@/components/shop/ShopBrowse";

function titleFromSlug(s: string) {
  return s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const Route = createFileRoute("/shop/category/$category")({
  head: ({ params }) => ({
    meta: [
      { title: `${titleFromSlug(params.category)} — Shop | Recovery Trauma™` },
      { name: "description", content: `Browse ${titleFromSlug(params.category)} in the Recovery Trauma™ wellbeing shop.` },
      { property: "og:title", content: `${titleFromSlug(params.category)} — Recovery Trauma™ Shop` },
      { property: "og:description", content: "Gently curated tools and resources for your healing journey." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `https://recoverytrauma.com/shop/category/${params.category}` }],
  }),
  component: CategoryPage,
});

function CategoryPage() {
  const { category } = Route.useParams();
  const hideCommerce = useHideCommerce();
  const fetchProducts = useServerFn(listShopProducts);
  const fetchCategories = useServerFn(listShopCategories);
  const { data, isLoading } = useQuery({ queryKey: ["shop", "products"], queryFn: () => fetchProducts() });
  const { data: catData } = useQuery({ queryKey: ["shop", "categories"], queryFn: () => fetchCategories() });
  const categories = catData?.categories ?? [];
  const cat = categories.find((c) => c.slug === category);
  const products = (data?.products ?? []).filter((p) => p.category_slug === category);

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-10">
      <Link to="/shop" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All categories
      </Link>
      <header className="mb-6 text-center">
        {cat && (
          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <CategoryIcon name={cat.icon} className="h-6 w-6 text-primary" />
          </span>
        )}
        <h1 className="text-2xl font-semibold sm:text-4xl">{cat?.name ?? titleFromSlug(category)}</h1>
        {cat?.description && <p className="mx-auto mt-2 max-w-xl text-muted-foreground">{cat.description}</p>}
      </header>

      {hideCommerce ? (
        <p className="rounded-2xl border bg-card p-6 text-center text-muted-foreground">The shop is available on our website.</p>
      ) : category === "gift-memberships" ? (
        <GiftMemberships />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" /></div>
      ) : (
        <ShopFilter products={products} categories={categories}>
          {products.length ? <ShopGrid products={products} /> : (
            <p className="rounded-2xl border bg-card p-6 text-center text-muted-foreground">New items are coming soon to this category.</p>
          )}
        </ShopFilter>
      )}
      <AffiliateDisclosure />
    </main>
  );
}
